/**
 * Settings page switches of the curve editor preferences (W2-04b curve-editor).
 *
 * The page's default boolean row shows a switch as on only when the stored value is `true`, but the curve editor
 * treats an unset Σ / ghosts / distance preference as ON (curveSettings.ts). These rows read and write through
 * `readCurveSetting` / `setCurveSetting` instead, so the switch always shows what the editor draws, the first click
 * turns the overlay off, and switching back to the default clears the stored value (Reset and "changed" stay right).
 */

import { useCallback, useSyncExternalStore, type ComponentType } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import { Switch } from '../../ui';
import { readCurveSetting, setCurveSetting, type CurveSettingKey } from './curveSettings';

function useCurveSetting(services: AppServices, key: CurveSettingKey): boolean {
  const subscribe = useCallback((cb: () => void) => services.settings.subscribe(cb), [services]);
  const read = useCallback(() => readCurveSetting(services.settings, key), [services, key]);
  return useSyncExternalStore(subscribe, read, read);
}

function useLanguage(services: AppServices): string {
  const subscribe = useCallback((cb: () => void) => services.i18n.subscribe(cb), [services]);
  const read = useCallback(() => services.i18n.locale, [services]);
  return useSyncExternalStore(subscribe, read, read);
}

/** The switch of one preference; `title` is the row title (the switch's accessible name). */
export function curveSettingSwitch(
  key: CurveSettingKey,
  title: MessageKey,
): ComponentType<{ readonly services: AppServices }> {
  function CurveSettingSwitch(props: { readonly services: AppServices }) {
    const { services } = props;
    const on = useCurveSetting(services, key);
    useLanguage(services);
    return (
      <Switch
        label={<span className="visually-hidden">{services.i18n.t(title)}</span>}
        checked={on}
        onChange={(v) => {
          setCurveSetting(services.settings, key, v);
        }}
      />
    );
  }
  CurveSettingSwitch.displayName = `CurveSettingSwitch(${key})`;
  return CurveSettingSwitch;
}
